import { usePageTitle } from '../hooks/usePageTitle';
import PageWrapper from '../components/PageWrapper';
import { Truck, RotateCcw, Headphones, Award, Clock, Package } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';

export default function ServicesPage() {
  usePageTitle("Nos services — Lodiss");

  const { lang } = useApp();
  const T = t[lang];

  const mainServices = [
    {
      icon: <Truck size={22} className="text-lodiss-red" />,
      title: T.services.deliveryTitle,
      content: (
        <ul className="space-y-3 mt-3">
          {T.services.deliveryItems.map((item, i) => (
            <li key={i} className="flex items-start justify-between gap-4 text-sm pb-3 border-b border-gray-50 last:border-0 last:pb-0">
              <span className="text-gray-600">{item.label}</span>
              <span className="font-semibold text-gray-900 text-right whitespace-nowrap">{item.value}</span>
            </li>
          ))}
        </ul>
      ),
    },
    {
      icon: <RotateCcw size={22} className="text-lodiss-red" />,
      title: T.services.returnsTitle,
      content: <p className="text-sm text-gray-600 mt-3 leading-relaxed">{T.services.returnsDesc}</p>,
    },
    {
      icon: <Headphones size={22} className="text-lodiss-red" />,
      title: T.services.supportTitle,
      content: <p className="text-sm text-gray-600 mt-3 leading-relaxed">{T.services.supportDesc}</p>,
    },
  ];

  const certifications = [
    { label: 'EN ISO 20471', desc: lang === 'fr' ? 'Vêtements haute visibilité' : 'High-visibility clothing' },
    { label: 'EN 397', desc: lang === 'fr' ? 'Casques de protection industrielle' : 'Industrial safety helmets' },
    { label: 'EN ISO 20345', desc: lang === 'fr' ? 'Chaussures de sécurité' : 'Safety footwear' },
    { label: 'EN 388', desc: lang === 'fr' ? 'Gants de protection mécanique' : 'Mechanical protective gloves' },
    { label: 'EN 149 FFP2', desc: lang === 'fr' ? 'Demi-masques filtrants' : 'Filtering half masks' },
    { label: 'IEC 62133', desc: lang === 'fr' ? 'Batteries portables' : 'Portable batteries' },
    { label: 'NF C 71-820', desc: lang === 'fr' ? 'BAES / éclairage de sécurité' : 'Emergency lighting' },
    { label: 'IEC 60896-21', desc: lang === 'fr' ? 'Batteries pour UPS' : 'Batteries for UPS' },
  ];

  return (
    <PageWrapper>
    <main className="bg-white min-h-screen">
      <section className="border-b border-gray-100 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-lodiss-red text-xs font-semibold uppercase tracking-widest mb-3 block">
            {lang === 'fr' ? 'Nos engagements' : 'Our commitments'}
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-950">{T.services.title}</h1>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            {mainServices.map((srv, i) => (
              <div key={i} className="bg-white border border-gray-100 rounded-xl p-6 hover:shadow-sm transition-shadow">
                <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center mb-4">
                  {srv.icon}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">{srv.title}</h3>
                {srv.content}
              </div>
            ))}
          </div>

          {/* Certifications */}
          <div>
            <h2 className="text-xl font-bold text-gray-950 mb-6 flex items-center gap-2">
              <Award size={20} className="text-lodiss-red" />
              {lang === 'fr' ? 'Normes & certifications produits' : 'Product standards & certifications'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {certifications.map((cert, i) => (
                <div key={i} className="border border-gray-100 rounded-lg p-4 hover:border-lodiss-red/20 transition-colors">
                  <p className="text-sm font-bold text-gray-900 font-mono mb-1">{cert.label}</p>
                  <p className="text-xs text-gray-500">{cert.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* B2B commitment strip */}
          <div className="mt-12 bg-gray-950 rounded-xl p-6 sm:p-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: <Clock size={18} />, title: lang === 'fr' ? 'Réponse devis < 24h' : 'Quote response < 24h', desc: lang === 'fr' ? 'Engagement de réponse rapide pour tous les devis volume.' : 'Fast response commitment for all volume quotes.' },
              { icon: <Package size={18} />, title: lang === 'fr' ? 'Stock vérifiable' : 'Verifiable stock', desc: lang === 'fr' ? 'Disponibilité affichée en temps réel sur chaque produit.' : 'Real-time availability displayed on each product.' },
              { icon: <Award size={18} />, title: lang === 'fr' ? '100% certifié' : '100% certified', desc: lang === 'fr' ? 'Tous nos produits sont conformes aux normes EN/ISO en vigueur.' : 'All our products comply with current EN/ISO standards.' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="text-lodiss-red flex-shrink-0 mt-0.5">{item.icon}</span>
                <div>
                  <p className="text-white text-sm font-semibold">{item.title}</p>
                  <p className="text-gray-400 text-xs mt-1 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
    </PageWrapper>
  );
}
